import type { ReactNode } from 'react'
import type { FreeFormObject } from '@ctcells/contract'

import { bundledDefinition, REST_CELLS, TC_STOCK_SLOTS } from './deckRackDefinitions.ts'
import {
  isTiprackDefinition,
  opentronsLayout,
  pocketClearing,
  type DefinitionLayout,
} from './opentronsLayout.ts'
import { RackPlate } from './RackPlate.tsx'
import { DECK_PLATE_LONG, TC_PLATE_H, TC_PLATE_W } from './rackPlateGeometry.ts'

import './figures.css'

/**
 * **데크 위의 실물 랙 한 장** — 염색기의 팁 랙 · 튜브 랙 1 · 튜브 랙 2(eric 2026-09-26 사진 셋) ·
 * 냉장 24구 랙, 어셈블러의 TC 적재소(eric 2026-09-27 「어셈블러의 TC 적재소 그림도 넣어줘」).
 *
 * 🔴 **다섯 랙은 «같은 판»이다** — 사진에서 옮긴 것:
 *    - 바깥 판은 **모서리가 직각인 직사각형**(모따기 없음)
 *    - 한 단 낮은 **포켓**이 판 안에 파여 있고, 모따기는 판이 아니라 **포켓의 네 귀**다(팔각)
 *    - **나사 구멍 넷**은 판의 네 귀 — 포켓 모따기 바깥의 삼각 살에 선다
 *    - **홈 둘**은 포켓 긴 변 가운데서 살 쪽으로 파인 반원
 *    ⇒ 판은 `RackPlate` 한 곳에서 그리고(준비 › 랩웨어 그림도 같은 판이다), 랙마다 갈리는 것은
 *      **구멍 배치뿐**이다.
 *
 * 🔴 **세로(긴 변이 위아래)로 그린다 — TC 적재소만 가로다**(아래 `slotsOf` · eric 2026-09-28 「그림을 90도로
 *    회전 시켜줘」)(eric 2026-09-26 「90도 회전하라는 게 각각이 아니라 전체를
 *    회전하라는 거였어」). 비전 화면(`…_01.jpg`)의 가로 데크 2×2 를 **통째로 시계 방향 90°** 돌린 모양이다 —
 *    랙 하나하나가 세로로 서고 홈 둘은 **좌우** 긴 변 가운데에 온다. 팁 랙은 12행 × 8열(사진 `…_02.jpg`),
 *    10구 튜브 랙은 데크에서 왼쪽이던 작은 원 무리가 **위**, 오른쪽이던 큰 원 2×2 가 **아래**다
 *    (사진 `…_03.jpg` 를 거꾸로 세운 방향). ⚠ 가로로 되돌리지 마라.
 *    ⚠ CSS `rotate` 가 아니라 **좌표로** 세운다 — 격자 칸 크기 · 접근성 · 시험이 그림 그대로 읽힌다.
 *
 * 🔴 **모르면 «가득»이다**(eric 2026-09-27 「저거 상황을 모르면 일단 다 채워져 있는 것으로 해줘」 ·
 *    「작업 진행하면서 실제로 저기에 있는 거 빼거나 하도록 할 예정이야」). 종전에는 「모르는 것을 그리지
 *    않는다」로 구멍을 전부 빈 원으로 뒀는데 **eric 이 뒤집었다** — 그 규칙으로 되돌리지 마라.
 *    칸마다 상태(`WellFill`)를 `occupancy` 로 받고, 없는 칸은 `full` 이다. 상태를 정하는 곳은
 *    `screens/monitoring/deckOccupancy.ts` 한 곳이다(서버 값이 들어올 자리도 거기 적혀 있다).
 *
 * | 상태      | 팁 랙                 | 튜브 랙                              | TC 적재소          |
 * | --------- | --------------------- | ------------------------------------ | ------------------ |
 * | `full`    | 구멍 + 팁 머리(채운 원) | 구멍 + 뚜껑(채운 원 + 안쪽 밝은 테)    | 칸 + TC 판(채운 사각) |
 * | `used`    | 빈 구멍(테만)          | 빈 구멍(테만)                         | 빈 칸(실선 테)      |
 * | `unknown` | 구멍 + 옅은 채움(무늬 없음) — 이번엔 부르는 곳이 없다                          ||
 *
 * 🔴 **색만으로 가르지 않는다** — 상태는 **채움 노드의 유무와 모양**이 말한다(`used` 는 채움 노드 0).
 *    시약 표시(`reagents`)도 색이 아니라 **뚜껑 가장자리 테 노드**가 있느냐로 선다 — 레시피의
 *    `display_color` 는 그 테에 얹을 뿐이고 없으면 무채색 테다.
 * ⚠ `used` 의 TC 칸을 점선으로 두지 않았다 — 점선은 「아직 없음(대기 · 모름)」 한 뜻이고(`ChamberFigure`),
 *    「썼다 · 뺐다」는 이미 일어난 사실이다. 팁 · 튜브의 빈 구멍과 같은 실선 테로 맞췄다.
 *
 * 🔴 **글자가 없다**(eric 2026-09-26 「일단 텍스트는 안 넣어도 돼」). 이름은 `aria-label` 로만 선다.
 * 🔴 색 리터럴 없음 — 클래스만 달고 색은 `figures.css` 의 토큰이 정한다.
 */
export type DeckRackKind = 'tips' | 'tubes4x6' | 'tubes6' | 'tubes24' | 'tcstock'

/** 칸 하나의 상태 — 위 머리말 표. 모르면 `full`(eric 2026-09-27) */
export type WellFill = 'full' | 'used' | 'unknown'

/**
 * 칸 열쇠 → 상태. 열쇠는 **정의의 웰 이름**(`A1` …)이다. 이름이 없는 칸(TC 적재소 9칸 · 정의에서 이름을
 * 짝짓지 못한 구멍)은 **1-기반 차례 문자열**(`'1'` …)이다 — 그림의 `data-well` 이 같은 열쇠를 단다.
 * 없는 열쇠는 `REST_CELLS`(`'*'`) 열쇠의 상태, 그것도 없으면 `full`.
 */
export type RackOccupancy = Readonly<Record<string, WellFill>>

/** 칸 열쇠 → 시약 표시색(`#RRGGBB`) · 색이 없으면 `null`(무채색 테). 열쇠에 없는 칸은 시약 없음 */
export type RackReagents = Readonly<Record<string, string | null>>

/** 데크 랙 판의 긴 변(세로 판 높이) — Opentrons 정의를 이 길이에 맞춘다 */
const H = DECK_PLATE_LONG
/**
 * TC 적재소 그림의 실제 폭 · 높이 — 가로로 긴 판(eric 2026-10-19 실물 사진 「tc 랙은 이렇게 해줘」 · 긴 변 : 짧은 변 ≈ 2.3).
 * 왼쪽 끝은 둥글고 손잡이 구멍이 뚫렸다 · 오른쪽 끝은 각졌다 · 나사 · 홈 · 포켓은 없다(`TcPlate`)
 */
const TC_W = TC_PLATE_W
const TC_H = TC_PLATE_H

/**
 * 🔴 **Opentrons 랩웨어 네 종류 — 그림은 «정의»가 정한다**(eric 2026-09-27 「이 랩웨어가 오픈트론의
 *    랩웨어야? 거기에 정보가 있으면 거기서 가지고 와서 그려.」). 판 치수 · 구멍 자리 · 지름은
 *    `opentronsLayout()` 이 정의에서 옮긴다 — 종전의 사진에서 잰 좌표 · 반지름 표는 걷었다.
 *
 * | 종류       | 레시피 자리(v4.6)                      | `loadName` |
 * | ---------- | -------------------------------------- | ---------- |
 * | `tips`     | `tips_1000_a` · `tips_1000_b`          | `opentrons_96_tiprack_1000ul` |
 * | `tubes6`   | `staining.reagent_bay.ambient.1`       | `opentrons_6_tuberack_falcon_50ml_conical` |
 * | `tubes4x6` | `staining.reagent_bay.ambient.2`       | `opentrons_10_tuberack_falcon_4x50ml_6x15ml_conical` |
 * | `tubes24`  | `staining.reagent_bay.chilled`         | `opentrons_24_aluminumblock_nest_1.5ml_snapcap` |
 *
 * **정의를 얻는 차례**: ① 부르는 쪽이 넘긴 `definition`(도는 작업의 레시피 `payload.labware[]` 에서 그 자리
 * id 로 찾은 것) ② 그것이 없거나 그릴 수 없으면 번들 사본(`@ctcells/fixtures` `OPENTRONS_DEFINITIONS`).
 * 어느 쪽으로 섰는지는 `data-definition`(`recipe` · `bundled`)이 말한다.
 * ⚠ TC 적재소(`tcstock`)는 우리 전용 랩웨어라 Opentrons 정의가 없다 — 아래 칸 표 그대로다.
 */
/* 표 본체는 `deckRackDefinitions.ts` 에 있다(요약 판이 칸 수를 같이 읽는다) */

/**
 * 데크 판은 세로(시계 방향 90°) · 긴 변을 TC 적재소 판과 같은 126 단위로(비율은 정의 그대로).
 * `standard` 면 돌리지 않는다 — Opentrons 표준 방향(A1 왼쪽 위 · 글자 = 행 · 숫자 = 열 · 가로 판).
 * ⚠ 지금 부르는 화면이 없다 — 통합 콘솔 소모품 판이 2026-10-12 에 썼다가(eric 「A, B, C 이게 행이 되어야」) 2026-10-13 eric 「다시
 *   세로로 해줘」로 세로에 돌아왔다. 옵션은 남긴다(다시 가로를 고르면 소모품 판 두 자리 · `rackLayoutOf` 를 같이 바꾼다)
 */
function deckLayout(def: FreeFormObject | undefined, standard = false): DefinitionLayout | null {
  return def === undefined ? null : opentronsLayout(def, { rotate: standard ? 0 : 90, fitLong: H })
}

interface ResolvedRack {
  layout: DefinitionLayout
  tiprack: boolean
  from: 'recipe' | 'bundled'
}

function resolveRack(
  kind: Exclude<DeckRackKind, 'tcstock'>,
  definition: FreeFormObject | undefined,
  standard = false,
): ResolvedRack | null {
  const fromRecipe = deckLayout(definition, standard)
  if (fromRecipe !== null && definition !== undefined) {
    return { layout: fromRecipe, tiprack: isTiprackDefinition(definition), from: 'recipe' }
  }
  const bundled = bundledDefinition(kind)
  const fromBundle = deckLayout(bundled, standard)
  return fromBundle === null
    ? null
    : { layout: fromBundle, tiprack: isTiprackDefinition(bundled), from: 'bundled' }
}

/**
 * **TC 적재소 칸 하나** — 슬라이드가 서는 직사각 칸(레시피 랩웨어 `assembler.tc_stock` · 18×36mm = 1:2).
 * 중심 · 폭 · 높이. 원이 아니라 세로로 긴 사각이다.
 */
interface Slot {
  x: number
  y: number
  w: number
  h: number
}

/*
 * TC 적재소 — 9칸을 **3행 × 3열**, 가로로 긴 칸(약 2 : 1)으로. 손잡이(왼쪽) 오른쪽에 붙어 판 높이를 거의 다 쓴다(eric 2026-10-19 사진).
 * ⚠ 칸 이름(T1~T9)의 자리 차례는 레시피에 없다 — 글자도 번호도 그리지 않는다.
 * 🔴 **칸 열쇠(`'1'` … `'9'`)의 자리는 종전 그대로다** — 오른쪽 열 위 → 아래가 1 · 2 · 3, 가운데 열이 4 · 5 · 6, 왼쪽 열이 7 · 8 · 9
 *    (2026-09-28 시계 방향 90° 돌린 판의 차례). 판 모양만 바꿨다 — 쓴 칸 표지가 다른 칸으로 옮지 않는다.
 */
const SLOT_W = 42
const SLOT_H = 20
const SLOT_GAP_X = 4
const SLOT_GAP_Y = 3
/** 칸 묶음의 왼쪽(손잡이 구멍 오른쪽) · 위 */
const SLOT_X0 = 34
const SLOT_Y0 = (TC_H - (SLOT_H * 3 + SLOT_GAP_Y * 2)) / 2

function slotsOf(kind: DeckRackKind): readonly Slot[] {
  if (kind !== 'tcstock') return []
  return Array.from({ length: TC_STOCK_SLOTS }, (_unused, n) => {
    const col = 2 - Math.floor(n / 3)
    const row = n % 3
    /* 칸 가운데(`Slot` 은 가운데 · 폭 · 높이다 — `cellsOf` 가 그대로 `cx` · `cy` 로 쓴다) */
    return {
      x: SLOT_X0 + col * (SLOT_W + SLOT_GAP_X) + SLOT_W / 2,
      y: SLOT_Y0 + row * (SLOT_H + SLOT_GAP_Y) + SLOT_H / 2,
      w: SLOT_W,
      h: SLOT_H,
    }
  })
}

/** TC 적재소 판 — 왼쪽 귀 둥긂(R) · 오른쪽 귀 각짐 + 왼쪽 손잡이 구멍(뚫림 · `evenodd`). 숫자는 사진 비율이다 */
const TC_R = 12
const TC_HANDLE = { x: 7, y: 13, w: 21, h: 49, r: 4 } as const
const TC_PLATE_PATH = (() => {
  const f = (v: number) => String(v)
  const e = 0.5
  const { x, y, w, h, r } = TC_HANDLE
  const outer = `M${f(e + TC_R)} ${f(e)}H${f(TC_W - e)}V${f(TC_H - e)}H${f(e + TC_R)}A${f(TC_R)} ${f(TC_R)} 0 0 1 ${f(e)} ${f(TC_H - e - TC_R)}V${f(e + TC_R)}A${f(TC_R)} ${f(TC_R)} 0 0 1 ${f(e + TC_R)} ${f(e)}Z`
  const hole = `M${f(x + r)} ${f(y)}H${f(x + w - r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + w)} ${f(y + r)}V${f(y + h - r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + w - r)} ${f(y + h)}H${f(x + r)}A${f(r)} ${f(r)} 0 0 1 ${f(x)} ${f(y + h - r)}V${f(y + r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + r)} ${f(y)}Z`
  return `${outer} ${hole}`
})()

function TcPlate() {
  return (
    <path className="fig-mod__plate" d={TC_PLATE_PATH} data-part="tc-plate" fillRule="evenodd" />
  )
}

const f = (v: number) => String(v)

/** 그림 좌표 두 자리 — 정의 mm × 배율이라 긴 소수가 나온다 */
const r2 = (v: number) => Math.round(v * 100) / 100

/** 레시피 `display_color` 의 꼴(`recipe.schema.json` · `#RRGGBB`) — 그 밖의 값은 그림에 싣지 않는다 */
const HEX6 = /^#[0-9A-Fa-f]{6}$/u

/** 그림 칸 한 개 — 구멍의 모양 · 자리 + 그 칸의 열쇠 · 갈래 */
type Cell = { key: string; well: string | undefined } & (
  | { part: 'tip' | 'tube'; shape: 'circle'; cx: number; cy: number; r: number }
  | { part: 'slot'; shape: 'rect'; cx: number; cy: number; w: number; h: number }
)

function cellsOf(kind: DeckRackKind, rack: ResolvedRack | null): Cell[] {
  if (rack === null) {
    return slotsOf(kind).map((slot, n) => ({
      key: String(n + 1),
      well: undefined,
      part: 'slot',
      shape: 'rect',
      cx: slot.x,
      cy: slot.y,
      w: slot.w,
      h: slot.h,
    }))
  }
  return rack.layout.holes.map((hole, n): Cell => {
    const key = hole.name ?? String(n + 1)
    const well = hole.name ?? undefined
    return hole.shape === 'circle'
      ? {
          key,
          well,
          part: rack.tiprack ? 'tip' : 'tube',
          shape: 'circle',
          cx: r2(hole.cx),
          cy: r2(hole.cy),
          r: r2(hole.r),
        }
      : {
          key,
          well,
          part: 'slot',
          shape: 'rect',
          cx: hole.cx,
          cy: hole.cy,
          w: r2(hole.w),
          h: r2(hole.h),
        }
  })
}

/** 구멍 — 상태와 무관하게 언제나 선다(판에 뚫린 자리) */
function Hole({ cell, bored = false }: { cell: Cell; bored?: boolean }) {
  if (cell.shape === 'circle' && bored) {
    /*
     * 알루미늄 블록의 구멍 — 파인 깊이가 보이게 안쪽 위가 그늘이다(사진 · 빛은 아래 벽에 받는다).
     * 바깥 원(구멍 테)은 종전 구멍과 같은 노드라 채움(뚜껑)이 그 위에 그대로 얹힌다
     */
    return (
      <g>
        <circle
          className={`fig-mod__hole fig-mod__hole--${cell.part}`}
          cx={cell.cx}
          cy={cell.cy}
          data-well={cell.well}
          r={cell.r}
        />
        <circle
          className="fig-mod__bore"
          cx={cell.cx}
          cy={r2(cell.cy - cell.r * BORE_SHIFT)}
          r={r2(cell.r * BORE_R)}
        />
      </g>
    )
  }
  return cell.shape === 'circle' ? (
    <circle
      className={`fig-mod__hole fig-mod__hole--${cell.part}`}
      cx={cell.cx}
      cy={cell.cy}
      data-well={cell.well}
      r={cell.r}
    />
  ) : (
    <rect
      className="fig-mod__hole fig-mod__hole--slot"
      data-well={cell.well}
      height={cell.h}
      rx={1.5}
      width={cell.w}
      x={r2(cell.cx - cell.w / 2)}
      y={r2(cell.cy - cell.h / 2)}
    />
  )
}

/*
 * 채움 비율 — 구멍 반지름(칸 크기)에 대한 상대값이라 튜브 크기(50 · 15 · 1.5 mL)마다 정의 지름을 따른다.
 * 팁 머리는 구멍보다 작게(구멍 테가 둘레에 보인다) · 뚜껑은 구멍을 거의 덮는다.
 */
/** 블록 구멍 안 그늘 — 구멍 반지름에 대한 상대값(위로 0.2 · 반지름 0.78 → 아래쪽에 밝은 초승달이 남는다) */
const BORE_SHIFT = 0.2
const BORE_R = 0.78
/** 팁 머리 — 구멍 반지름에 대한 비(사진의 검정 팁이 구멍을 거의 덮는다 · 종전 0.72) · 입구 고리 */
const TIP_HEAD = 0.9
const TIP_MOUTH = 0.58
const CAP = 0.86
const CAP_INNER = 0.52
const RIM_W = 0.14
/** 칸 가운데 글자(`marks`)의 크기 — 구멍 반지름 대비. 한 자리 수가 뚜껑(`CAP`) 지름 안에 들면서 작은 칸(냉장 24구)에서도 읽힌다 */
const MARK = 1.3
/**
 * 칸 글자 크기 상한(판 단위) — 큰 튜브(50 mL)에서 글자가 구멍 크기를 따라 너무 커지지 않게 냉장 24구 글자 크기 언저리에서 멈춘다
 * (eric 2026-10-20 「해당 튜브에.. 숫자가 너무 커.. 숫자 좀 줄여줘」)
 */
const MARK_MAX = 5
const TC_INSET = 1.6

/**
 * 칸 안의 채움 — `used` 면 **아무것도 그리지 않는다**(빈 구멍). 채움 노드의 유무가 곧 상태다.
 */
function Fill({
  cell,
  fill,
  reagent,
}: {
  cell: Cell
  fill: WellFill
  reagent: string | null | undefined
}) {
  if (fill === 'used') return null
  if (fill === 'unknown') {
    return cell.shape === 'circle' ? (
      <circle
        className="fig-mod__fill fig-mod__fill--unknown"
        cx={cell.cx}
        cy={cell.cy}
        r={r2(cell.r * CAP)}
      />
    ) : (
      <rect
        className="fig-mod__fill fig-mod__fill--unknown"
        height={r2(cell.h - 2 * TC_INSET)}
        rx={1}
        width={r2(cell.w - 2 * TC_INSET)}
        x={r2(cell.cx - cell.w / 2 + TC_INSET)}
        y={r2(cell.cy - cell.h / 2 + TC_INSET)}
      />
    )
  }
  if (cell.shape === 'rect') {
    return (
      <rect
        className="fig-mod__fill fig-mod__fill--tc"
        height={r2(cell.h - 2 * TC_INSET)}
        rx={1}
        width={r2(cell.w - 2 * TC_INSET)}
        x={r2(cell.cx - cell.w / 2 + TC_INSET)}
        y={r2(cell.cy - cell.h / 2 + TC_INSET)}
      />
    )
  }
  if (cell.part === 'tip') {
    /*
     * 팁 — 위에서 본 검정 팁 머리(eric 2026-10-19 팁 랙 사진 「검정색.. 이 팁이 차 잇는 거고」). 구멍을 거의 덮는 검정 원 + 안쪽 입구 고리 한 줄.
     * 빈 칸(쓴 팁)은 종전대로 이 노드가 없어 밝은 구멍만 남는다
     */
    return (
      <g className="fig-mod__fill fig-mod__fill--tip">
        <circle className="fig-mod__tip-head" cx={cell.cx} cy={cell.cy} r={r2(cell.r * TIP_HEAD)} />
        <circle
          className="fig-mod__tip-mouth"
          cx={cell.cx}
          cy={cell.cy}
          r={r2(cell.r * TIP_MOUTH)}
        />
      </g>
    )
  }
  const color = typeof reagent === 'string' && HEX6.test(reagent) ? reagent : undefined
  return (
    <g className="fig-mod__fill fig-mod__fill--tube">
      <circle className="fig-mod__cap" cx={cell.cx} cy={cell.cy} r={r2(cell.r * CAP)} />
      <circle className="fig-mod__cap-ring" cx={cell.cx} cy={cell.cy} r={r2(cell.r * CAP_INNER)} />
      {reagent === undefined ? null : (
        /* 시약이 담긴 튜브 — 뚜껑 가장자리 테. 색은 레시피 `display_color` · 없으면 무채색(CSS) */
        <circle
          className="fig-mod__rim"
          cx={cell.cx}
          cy={cell.cy}
          data-color={color}
          r={r2(cell.r * (CAP - RIM_W / 2))}
          strokeWidth={r2(cell.r * RIM_W)}
          style={color === undefined ? undefined : { stroke: color }}
        />
      )}
    </g>
  )
}

/**
 * **알루미늄 블록**(냉장 24구 · eric 2026-10-18 사진) — 모서리가 살짝 둥근 통짜 직사각 블록. 윗면 가장자리의 밝은 모따기 한 줄이
 * 금속 블록의 두께를 말한다. 나사 · 홈 · 포켓은 없다(`RackPlate` 와 다른 물건). 🔴 색 리터럴 없음 — 클래스만
 */
function BlockPlate({ width, height }: { width: number; height: number }) {
  return (
    <>
      <rect
        className="fig-mod__block"
        height={height - 1}
        rx={4}
        width={width - 1}
        x={0.5}
        y={0.5}
      />
      <rect
        className="fig-mod__block-face"
        height={height - 6}
        rx={2.5}
        width={width - 6}
        x={3}
        y={3}
      />
    </>
  )
}

export function DeckRackFigure({
  kind,
  label,
  definition,
  occupancy,
  reagents,
  marks,
  overlay,
  standard = false,
}: {
  kind: DeckRackKind
  label: string
  /** Opentrons 표준 방향(돌리지 않은 가로 판 · 글자 = 행)으로 — `deckLayout` 머리말. TC 적재소는 늘 같다 */
  standard?: boolean | undefined
  /** 도는 작업의 레시피가 그 자리에 싣는 랩웨어 정의 — 없으면 번들 사본(`LOAD_NAME` 머리말) */
  definition?: FreeFormObject | undefined
  /** 칸별 상태 — 없는 칸은 `full`(머리말). 출처는 `deckOccupancy()` 한 곳 */
  occupancy?: RackOccupancy | undefined
  /** 시약이 담긴 칸(튜브 랙) — 도는 작업 레시피가 있을 때만 온다(`deckReagentsAt()`) */
  reagents?: RackReagents | undefined
  /**
   * 칸 열쇠 → 칸 가운데 글자(시약 번호 — 레시피 시약 배치 그림 · eric 2026-10-20 「이것... 실제 그림으로부터 그려줘」).
   * 안 넘기면 글자가 없다(모니터링 · 장비 관점 앱은 넘기지 않는다)
   */
  marks?: Readonly<Record<string, string>> | undefined
  /**
   * 판 위에 겹쳐 그릴 것 — 그림 좌표(판 폭 · 높이)를 받아 노드를 돌려준다. 모듈 앱 소모품 판이 «지금 다루는 랙» 표지
   * (깜빡이는 테 · 부분 피펫)를 판 크기에 맞춰 얹을 때 쓴다(`StainBinNow`). 안 넘기면 아무것도 없다
   */
  overlay?: ((box: { width: number; height: number }) => ReactNode) | undefined
}) {
  const rack = kind === 'tcstock' ? null : resolveRack(kind, definition, standard)
  const width = rack === null ? TC_W : r2(rack.layout.width)
  const height = rack === null ? TC_H : r2(rack.layout.height)
  const cells = cellsOf(kind, rack)
  const fills = cells.map((cell) => occupancy?.[cell.key] ?? occupancy?.[REST_CELLS] ?? 'full')
  /* 냉장 24구는 알루미늄 블록(eric 2026-10-18 사진 「이게 콜드랙이야」) — 나사 · 홈 · 포켓 판이 아니라 통짜 블록에 구멍만 파였다 */
  const block = kind === 'tubes24'
  return (
    <svg
      aria-label={label}
      className={`fig fig-mod fig-deckrack fig-deckrack--${kind}`}
      data-definition={rack?.from}
      data-filled={fills.filter((v) => v !== 'used').length}
      data-rack={kind}
      data-total={cells.length}
      height={height}
      role="img"
      strokeLinejoin="round"
      viewBox={`0 0 ${f(width)} ${f(height)}`}
      width={width}
    >
      {kind === 'tcstock' ? (
        <TcPlate />
      ) : block ? (
        <BlockPlate height={height} width={width} />
      ) : (
        <RackPlate
          height={height}
          pocket={rack === null ? undefined : pocketClearing(rack.layout)}
          /* 팁 랙에는 귀 나사가 없다(eric 2026-10-19 사진) — 튜브 랙만 */
          screws={kind !== 'tips'}
          width={width}
        />
      )}
      {cells.map((cell, n) => {
        const fill = fills[n] ?? 'full'
        return (
          <g className="fig-mod__cell" data-cell={cell.key} data-fill={fill} key={cell.key}>
            <Hole bored={block} cell={cell} />
            <Fill cell={cell} fill={fill} reagent={reagents?.[cell.key]} />
            {marks?.[cell.key] === undefined ? null : (
              <text
                className="fig-mod__mark"
                dominantBaseline="central"
                fontSize={r2(
                  Math.min(cell.shape === 'circle' ? cell.r * MARK : cell.h * MARK * 0.5, MARK_MAX),
                )}
                textAnchor="middle"
                x={cell.cx}
                y={cell.cy}
              >
                {marks[cell.key]}
              </text>
            )}
          </g>
        )
      })}
      {overlay?.({ width, height })}
    </svg>
  )
}
